import * as React from 'react';
import { Box, Dialog, IconButton, Theme, createStyles, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { Error } from '@pec/aion-ui-components/components/Error';
import { PRODUCTS_LOGOS } from 'helpers/newDashboard';
import { EssentialsBundleCard } from 'components/OnlineTraining/EssentialsBundleCard';
import { OnlineTrainingForm } from 'components/OnlineTraining/Form';
import { useAppDispatch, useTypedSelector } from 'combineReducers';
import { fetchThirdPartyProducts } from 'slices/thirdPartyProducts';
import { fetchOrganizationInfo } from '@pec/aion-ui-core/slices/organizationInfo';
import { round, isEmpty } from 'lodash';
import { handleCompletion } from 'helpers/complete';
import { fetchMainSubscription } from '@pec/aion-ui-core/slices/mainSubscription';
import { useTranslation } from 'react-i18next';
import { TaxIncludeLocation } from 'interfaces/onlineTraining';
import { formatPrice } from '../../helpers/formatPrice';

export const ONLINE_TRAINING_STORAGE_KEY = 'onlineTraining:paymentIsProcessing';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(3),
      height: '100%'
    },
    content: {
      display: 'flex',
      gap: '32px'
    },
    titleLogo: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(3)
    },
    title: {
      fontSize: 20,
      fontWeight: theme.typography.fontWeightMedium,
      marginLeft: theme.spacing(2)
    },
    dialogClose: {
      position: 'absolute',
      top: 8,
      right: 8
    }
  })
);

export const OnlineTrainingContainer: React.FC = () => {
  const { t } = useTranslation('subscriber');
  const dispatch = useAppDispatch();
  const { organizationInfo, isFetching: isFetchingOrganizationInfo } = useTypedSelector(
    state => state.organizationInfo
  );
  const { userInfo } = useTypedSelector(state => state.userInfo);
  const { mainSubscription } = useTypedSelector(state => state.mainSubscription);
  const { organization, isFetching: isFetchingOrganization } = useTypedSelector(state => state.organization);
  const { thirdPartyProducts, isFetching: isFetchingProducts } = useTypedSelector(state => state.thirdPartyProducts);

  const Logo = PRODUCTS_LOGOS.onlineTraining;
  const isFetching = isFetchingOrganization || isFetchingProducts || isFetchingOrganizationInfo;
  const { products, taxRate } = thirdPartyProducts;
  const classes = useStyles();

  const [productSelect, setProductSelect] = React.useState<string>('');
  const [workers, setWorkers] = React.useState<number>(0);
  const [totalPrice, setTotalPrice] = React.useState<string | number>(0);
  const [isTaxIncluded, setIsTaxIncluded] = React.useState<boolean>(false);
  const [formState, setFormState] = React.useState<{ isLoading: boolean; error?: string }>({ isLoading: false });

  React.useEffect(() => {
    if (organization) {
      dispatch(fetchOrganizationInfo(organization.id));
      dispatch(fetchMainSubscription(organization.id));
    }
  }, [dispatch, organization]);

  React.useEffect(() => {
    const { countryISO2 } = mainSubscription;

    if (organizationInfo && countryISO2) dispatch(fetchThirdPartyProducts({ organizationInfo, countryISO2 }));
  }, [dispatch, organizationInfo, mainSubscription]);

  React.useEffect(() => {
    getTotalPrice(productSelect);
    // eslint-disable-next-line
  }, [workers, productSelect]);

  const handleSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProductSelect(event.target.value);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWorkers(+event.target.value);
  };

  const getTotalPrice = (value: string) => {
    const { countryIso2Code } = organizationInfo || { countryIso2Code: '' };

    const product = products?.find(p => p.id === value) || { price: 0, location: 'GB' };

    const { price, location } = product;
    const totalPrice =
      taxRate && TaxIncludeLocation[countryIso2Code]
        ? ((product.price * 100 + round((price * taxRate.ratePercentage) / 100, 2) * 100) / 100) * workers
        : price * workers;

    setIsTaxIncluded(!!(taxRate && TaxIncludeLocation[countryIso2Code]));
    setTotalPrice(formatPrice(totalPrice, location));
  };

  const handlePayment = async () => {
    setFormState({ isLoading: true });

    if (organizationInfo && userInfo && mainSubscription) {
      try {
        const payload = { taxRate, organizationInfo, productSelect, userInfo, quantity: workers, mainSubscription };
        const { data } = await handleCompletion(payload);

        localStorage.setItem(ONLINE_TRAINING_STORAGE_KEY, 'true');
        window.location.href = data.checkoutUrl;
      } catch (error) {
        setFormState({ isLoading: false, error: 'Unknown error, please try again later.' });
      }
    }
  };

  const onDialogClose = () => {
    setFormState({ isLoading: false });
  };

  if (formState.isLoading) {
    return (
      <Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
        <Loading />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.titleLogo}>
        <Logo />
        <Box className={classes.title}>{t('onlineTraining.title', 'Online training')}</Box>
      </Box>

      <Box className={classes.content}>
        <EssentialsBundleCard />
        <OnlineTrainingForm
          products={products}
          isFetching={isFetching}
          productSelect={productSelect}
          handleSelectChange={handleSelectChange}
          handleInputChange={handleInputChange}
          totalPrice={totalPrice}
          taxRate={taxRate}
          isTaxIncluded={isTaxIncluded}
          isDisable={workers === 0 || isEmpty(productSelect)}
          handlePayment={handlePayment}
        />
      </Box>

      <Dialog open={!!formState.error} onClose={onDialogClose}>
        <IconButton className={classes.dialogClose} size="small" onClick={onDialogClose}>
          <CloseIcon />
        </IconButton>
        <Error message={formState.error} />
      </Dialog>
    </Box>
  );
};
